import { useCallback, useEffect, useState } from "react";
import { ArrowsClockwise, Sparkle, CurrencyCircleDollar, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QuoteCard, KindFilter, KIND_LABEL } from "@/components/MarketCards";
import { WorldBoard } from "@/components/WorldBoard";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export default function Markets() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canHold = ["admin", "vendor", "buyer"].includes(user?.role);
  const [view, setView] = useState("watch");
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  const [book, setBook] = useState(null);
  const [note, setNote] = useState(null);
  const [noteEngine, setNoteEngine] = useState("gpt");
  const [engine, setEngine] = useState("gpt");
  const [busy, setBusy] = useState(false);
  const [keyInfo, setKeyInfo] = useState(null);
  const [keyValue, setKeyValue] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [instForm, setInstForm] = useState({ name: "", kind: "index", symbol: "", country: "", currency: "usd", source: "manual", maturity_years: "", public: true });
  const [holdForm, setHoldForm] = useState({ name: "", kind: "equity", symbol: "", quantity: 1, unit_cost: 0, currency: "usd", current_price: "", purchase_date: "", maturity_date: "", coupon_pct: "" });

  const loadWatch = useCallback(() => {
    api.get(`/markets/watch${kind === "all" ? "" : `?kind=${kind}`}`)
      .then(({ data: d }) => setData(d)).catch(() => setData(null));
  }, [kind]);
  const loadBook = useCallback(() => {
    api.get("/markets/holdings").then(({ data: d }) => setBook(d)).catch(() => setBook(null));
  }, []);

  useEffect(loadWatch, [loadWatch]);
  useEffect(() => { if (view === "treasury") loadBook(); }, [view, loadBook]);
  useEffect(() => {
    if (isAdmin) api.get("/markets/settings").then(({ data }) => setKeyInfo(data)).catch(() => setKeyInfo(null));
  }, [isAdmin]);

  const saveKey = async () => {
    setSavingKey(true);
    try {
      const { data } = await api.post("/markets/settings/key", { key: keyValue.trim() });
      if (data.verified) {
        toast.success(`Market data key saved (…${data.last_four}) — live prices are on`);
      } else {
        toast.warning(data.note || "Saved — press Refresh prices to confirm it works");
      }
      setKeyValue("");
      const info = await api.get("/markets/settings");
      setKeyInfo(info.data);
      loadWatch();
    } catch (e) { oops(e); } finally { setSavingKey(false); }
  };

  const clearKey = async () => {
    try {
      await api.delete("/markets/settings/key");
      toast.success("Key removed — prices stay as you maintain them");
      const info = await api.get("/markets/settings");
      setKeyInfo(info.data);
      loadWatch();
    } catch (e) { oops(e); }
  };

  const setQuote = async (row, value, change) => {
    try {
      await api.post(`/markets/instruments/${row.id}/quote`, {
        value: Number(value), change_pct: change === "" ? null : Number(change),
      });
      toast.success(`${row.name} updated`);
      loadWatch();
    } catch (e) { oops(e); }
  };

  const removeInstrument = async (row) => {
    try {
      await api.delete(`/markets/instruments/${row.id}`);
      toast.success("Removed from the watchlist");
      loadWatch();
    } catch (e) { oops(e); }
  };

  const addInstrument = async () => {
    try {
      await api.post("/markets/instruments", {
        ...instForm,
        maturity_years: instForm.maturity_years === "" ? null : Number(instForm.maturity_years),
        symbol: instForm.symbol || null, country: instForm.country || null,
      });
      toast.success("Added to the watchlist");
      setInstForm({ ...instForm, name: "", symbol: "" });
      loadWatch();
    } catch (e) { oops(e); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/markets/refresh", {});
      toast.success(d.note || "Refreshing prices in the background");
      const stop = Date.now() + (d.estimated_seconds || 60) * 1000 + 20000;
      const poll = setInterval(async () => {
        try {
          const { data: s } = await api.get("/markets/refresh/status");
          loadWatch();
          if (!s.running || Date.now() > stop) {
            clearInterval(poll);
            setBusy(false);
            if (s.last_run?.note) {
              toast.warning(s.last_run.note);
            } else if (s.last_run) {
              toast.success(`${s.last_run.updated} price(s) updated`
                + (s.last_run.failed?.length ? `, ${s.last_run.failed.length} unavailable` : ""));
            }
          }
        } catch {
          clearInterval(poll);
          setBusy(false);
        }
      }, 15000);
    } catch (e) { oops(e); setBusy(false); }
  };

  const applyFx = async () => {
    try {
      const { data: d } = await api.post("/markets/fx/apply", {});
      toast.success(`Workspace rates updated: ${d.applied.join(", ")}`);
    } catch (e) { oops(e); }
  };

  const askCommentary = async (which = engine) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/markets/ai/commentary?engine=${which}`, {});
      setNote(d.commentary);
      setNoteEngine(d.engine || which);
      setEngine(which);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const addHolding = async () => {
    try {
      await api.post("/markets/holdings", {
        ...holdForm,
        quantity: Number(holdForm.quantity), unit_cost: Number(holdForm.unit_cost),
        current_price: holdForm.current_price === "" ? null : Number(holdForm.current_price),
        coupon_pct: holdForm.coupon_pct === "" ? null : Number(holdForm.coupon_pct),
        symbol: holdForm.symbol || null,
        purchase_date: holdForm.purchase_date || null,
        maturity_date: holdForm.maturity_date || null,
      });
      toast.success("Holding recorded");
      setHoldForm({ ...holdForm, name: "", symbol: "" });
      loadBook();
    } catch (e) { oops(e); }
  };

  const removeHolding = async (id) => {
    try {
      await api.delete(`/markets/holdings/${id}`);
      toast.success("Removed");
      loadBook();
    } catch (e) { oops(e); }
  };

  return (
    <div className="space-y-6" data-testid="markets-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-[#B3241B]">World markets</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F172A]">Shares, government bonds and currencies</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Where the markets closed, what government paper is yielding and where the currencies sit —
            plus your own holdings, and what the moves mean for your imports and freight.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button variant="outline" data-testid="mkt-refresh" onClick={refresh} disabled={busy}>
              <ArrowsClockwise size={16} className="mr-1.5" /> {busy ? "Working…" : "Refresh prices"}
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" data-testid="mkt-apply-fx" onClick={applyFx}>
              <CurrencyCircleDollar size={16} className="mr-1.5" /> Use these FX rates
            </Button>
          )}
          {canHold && (
            <Button variant="outline" data-testid="mkt-ai" onClick={() => askCommentary()} disabled={busy}>
              <Sparkle size={16} className="mr-1.5" /> What it means for us
            </Button>
          )}
        </div>
      </div>

      {data && !data.provider_ready && (
        <div data-testid="mkt-key-panel" className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3">
          <p className="text-sm font-semibold text-[#0F172A]">Switch on live prices</p>
          <p className="mt-1 max-w-2xl text-xs text-slate-600">
            Prices are maintained by hand at the moment. Get a free key at{" "}
            <a href="https://www.alphavantage.co/support/#api-key" target="_blank" rel="noopener noreferrer" className="tap-safe font-semibold text-[#002FA7] underline">
              alphavantage.co
            </a>{" "}
            (email and business name, no card) and paste it below — indices, bond yields, currencies,
            oil, gas, copper and aluminium then refresh themselves.
          </p>
          {isAdmin ? (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input data-testid="mkt-key-input" value={keyValue} onChange={(e) => setKeyValue(e.target.value)}
                placeholder="Paste your Alpha Vantage key" className="sm:max-w-xs" />
              <Button data-testid="mkt-key-save" onClick={saveKey} disabled={savingKey || keyValue.trim().length < 8}>
                {savingKey ? "Checking…" : "Save the key"}
              </Button>
            </div>
          ) : (
            <p className="mt-2 text-xs text-slate-500">An admin can add the key on this page.</p>
          )}
        </div>
      )}
      {data?.provider_ready && isAdmin && (
        <p data-testid="mkt-key-ok" className="border-l-2 border-[#0F7A3D] bg-[#EAF7EF] px-3 py-2 text-xs text-[#0B5C2E]">
          Live prices are on from the free feed — no key and no daily limit. Every instrument
          here refreshes on demand, and the morning job tops them up.
          {keyInfo?.configured
            ? ` Alpha Vantage${keyInfo.last_four ? ` (key ending ${keyInfo.last_four})` : ""} is kept as the backup, limited to 25 calls a day.`
            : " Add an Alpha Vantage key below if you want a backup source."}
          {keyInfo?.configured && !keyInfo.from_environment && (
            <button data-testid="mkt-key-clear" onClick={clearKey} className="tap-safe ml-2 font-semibold underline">Remove the backup key</button>
          )}
        </p>
      )}
      {note && (
        <div data-testid="mkt-ai-note" className="border-l-2 border-[#B3241B] bg-[#FFF6E5] px-4 py-3 text-sm text-[#0F172A]">
          <p className="whitespace-pre-line">{note}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#F0DEBC] pt-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Written by</span>
            {[["gpt", "GPT-5.5"], ["claude", "Claude Sonnet 4.6"]].map(([key, label]) => (
              <button key={key} data-testid={`mkt-engine-${key}`} disabled={busy}
                onClick={() => askCommentary(key)}
                className={`tap-safe border px-2.5 py-1 text-[11px] font-semibold transition-colors duration-150 disabled:opacity-60 ${
                  noteEngine === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="w-full scroll-x overflow-x-auto">
        <TabsList data-testid="mkt-tabs" className="w-max">
          <TabsTrigger value="watch" data-testid="mkt-tab-watch">Market watch</TabsTrigger>
          <TabsTrigger value="world" data-testid="mkt-tab-world">World board</TabsTrigger>
          <TabsTrigger value="treasury" data-testid="mkt-tab-treasury">Our holdings</TabsTrigger>
          {isAdmin && <TabsTrigger value="manage" data-testid="mkt-tab-manage">Watchlist</TabsTrigger>}
        </TabsList>
      </Tabs>

      {view === "watch" && (
        <div className="space-y-4" data-testid="mkt-watch">
          <KindFilter value={kind} onChange={setKind} />
          {!data ? <p className="py-10 text-center text-sm text-slate-500">Loading…</p>
            : data.instruments.length === 0 ? (
              <p data-testid="mkt-watch-empty" className="border border-border bg-white p-8 text-center text-sm text-slate-600">
                Nothing on the watchlist yet.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="mkt-cards">
                {data.instruments.map((row, i) => (
                  <QuoteCard key={row.id} row={row} index={i} canEdit={isAdmin} onQuote={setQuote} onRemove={removeInstrument} />
                ))}
              </div>
            )}
        </div>
      )}

      {view === "world" && (
        <div data-testid="mkt-world">
          <WorldBoard canRefresh={isAdmin} canResearch={canHold} />
        </div>
      )}

      {view === "treasury" && (
        <div className="flex flex-col gap-4" data-testid="mkt-treasury">
          <div className="order-1 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {(book?.totals || []).map((t, i) => (
              <div key={t.currency} data-testid={`mkt-total-${i}`} className="border border-border bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t.currency.toUpperCase()} value</p>
                <p className="mt-2 text-2xl font-bold text-[#0F172A]">{t.value.toLocaleString()}</p>
                <p className={`text-sm font-semibold ${t.gain >= 0 ? "text-[#0B5C2E]" : "text-[#B3241B]"}`}>
                  {t.gain >= 0 ? "+" : ""}{t.gain.toLocaleString()}{t.gain_pct != null ? ` (${t.gain_pct}%)` : ""}
                </p>
              </div>
            ))}
          </div>

          {canHold && (
          <div className="order-3 border border-border bg-white p-4 md:order-2">
            <p className="text-sm font-semibold text-[#0F172A]">Record a holding</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div><Label>Name</Label><Input data-testid="mkt-hold-name" value={holdForm.name} onChange={(e) => setHoldForm({ ...holdForm, name: e.target.value })} placeholder="10-year government bond" /></div>
              <div>
                <Label>Kind</Label>
                <select data-testid="mkt-hold-kind" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={holdForm.kind} onChange={(e) => setHoldForm({ ...holdForm, kind: e.target.value })}>
                  {Object.entries(KIND_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </div>
              <div><Label>Symbol (optional)</Label><Input data-testid="mkt-hold-symbol" value={holdForm.symbol} onChange={(e) => setHoldForm({ ...holdForm, symbol: e.target.value.toUpperCase() })} /></div>
              <div><Label>Currency</Label><Input data-testid="mkt-hold-currency" value={holdForm.currency} onChange={(e) => setHoldForm({ ...holdForm, currency: e.target.value.toLowerCase() })} /></div>
              <div><Label>Quantity</Label><Input data-testid="mkt-hold-qty" type="number" min={0} value={holdForm.quantity} onChange={(e) => setHoldForm({ ...holdForm, quantity: e.target.value })} /></div>
              <div><Label>Cost each</Label><Input data-testid="mkt-hold-cost" type="number" min={0} value={holdForm.unit_cost} onChange={(e) => setHoldForm({ ...holdForm, unit_cost: e.target.value })} /></div>
              <div><Label>Price now (optional)</Label><Input data-testid="mkt-hold-price" type="number" min={0} value={holdForm.current_price} onChange={(e) => setHoldForm({ ...holdForm, current_price: e.target.value })} /></div>
              <div><Label>Matures</Label><Input data-testid="mkt-hold-maturity" type="date" value={holdForm.maturity_date} onChange={(e) => setHoldForm({ ...holdForm, maturity_date: e.target.value })} /></div>
            </div>
            <Button className="mt-3" data-testid="mkt-hold-submit" onClick={addHolding}><Plus size={16} className="mr-1.5" /> Record it</Button>
          </div>
          )}

          {!book || book.holdings.length === 0 ? (
            <p data-testid="mkt-holdings-empty" className="order-2 border border-border bg-white p-8 text-center text-sm text-slate-600">No holdings recorded yet.</p>
          ) : (
            <>
            <div className="order-2 space-y-2 md:hidden" data-testid="mkt-holdings-cards">
              {book.holdings.map((h, i) => (
                <div key={h.id} data-testid={`mkt-holding-card-${i}`} className="border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-[#0F172A]">{h.name}</p>
                      <p className="text-xs text-slate-500">{KIND_LABEL[h.kind]}{h.symbol ? ` · ${h.symbol}` : ""} · {h.currency.toUpperCase()}</p>
                    </div>
                    <button data-testid={`mkt-holding-remove-card-${i}`} className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#B3241B]" onClick={() => removeHolding(h.id)}>Remove</button>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                    <p>Qty: <span className="text-[#0F172A]">{h.quantity}</span></p>
                    <p>Cost: <span className="text-[#0F172A]">{h.cost.toLocaleString()}</span></p>
                    <p>Value: <span className="text-[#0F172A]">{h.value != null ? h.value.toLocaleString() : "—"}</span></p>
                    <p>Matures: <span className="text-[#0F172A]">{h.days_to_maturity == null ? "—" : `${h.days_to_maturity} day(s)`}</span></p>
                  </div>
                  <p className={`mt-2 text-sm font-semibold ${h.gain == null ? "text-slate-400" : h.gain >= 0 ? "text-[#0B5C2E]" : "text-[#B3241B]"}`}>
                    {h.gain == null ? "—" : `${h.gain >= 0 ? "+" : ""}${h.gain.toLocaleString()}${h.gain_pct != null ? ` (${h.gain_pct}%)` : ""}`}
                  </p>
                </div>
              ))}
            </div>
            <div className="order-3 hidden overflow-x-auto border border-border bg-white md:order-3 md:block">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="p-3 text-left">Holding</th><th className="p-3 text-right">Qty</th>
                    <th className="p-3 text-right">Cost</th><th className="p-3 text-right">Value</th>
                    <th className="p-3 text-right">Gain</th><th className="p-3 text-right">Matures in</th><th className="p-3" />
                  </tr>
                </thead>
                <tbody data-testid="mkt-holdings-rows">
                  {book.holdings.map((h, i) => (
                    <tr key={h.id} className="border-t border-border" data-testid={`mkt-holding-${i}`}>
                      <td className="p-3">
                        <span className="font-semibold text-[#0F172A]">{h.name}</span>
                        <span className="block text-xs text-slate-500">{KIND_LABEL[h.kind]}{h.symbol ? ` · ${h.symbol}` : ""} · {h.currency.toUpperCase()}</span>
                      </td>
                      <td className="p-3 text-right">{h.quantity}</td>
                      <td className="p-3 text-right">{h.cost.toLocaleString()}</td>
                      <td className="p-3 text-right">{h.value != null ? h.value.toLocaleString() : "—"}</td>
                      <td className={`p-3 text-right font-semibold ${h.gain == null ? "text-slate-400" : h.gain >= 0 ? "text-[#0B5C2E]" : "text-[#B3241B]"}`}>
                        {h.gain == null ? "—" : `${h.gain >= 0 ? "+" : ""}${h.gain.toLocaleString()}${h.gain_pct != null ? ` (${h.gain_pct}%)` : ""}`}
                      </td>
                      <td className="p-3 text-right">{h.days_to_maturity == null ? "—" : `${h.days_to_maturity} day(s)`}</td>
                      <td className="p-3 text-right">
                        <button data-testid={`mkt-holding-remove-${i}`} className="text-xs font-semibold text-slate-500 hover:text-[#B3241B]" onClick={() => removeHolding(h.id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}

          {book?.maturing_soon?.length > 0 && (
            <div className="order-4 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3 text-sm md:order-4" data-testid="mkt-maturing">
              <p className="font-semibold text-[#0F172A]">Maturing next</p>
              <ul className="mt-1 space-y-1 text-slate-600">
                {book.maturing_soon.map((h) => <li key={h.id}>{h.name} — {h.days_to_maturity} day(s)</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {view === "manage" && isAdmin && (
        <div className="border border-border bg-white p-4" data-testid="mkt-manage">
          <p className="text-sm font-semibold text-[#0F172A]">Add to the watchlist</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div><Label>Name</Label><Input data-testid="mkt-inst-name" value={instForm.name} onChange={(e) => setInstForm({ ...instForm, name: e.target.value })} placeholder="Sensex" /></div>
            <div>
              <Label>Kind</Label>
              <select data-testid="mkt-inst-kind" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={instForm.kind} onChange={(e) => setInstForm({ ...instForm, kind: e.target.value })}>
                {Object.entries(KIND_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div><Label>Symbol / pair</Label><Input data-testid="mkt-inst-symbol" value={instForm.symbol} onChange={(e) => setInstForm({ ...instForm, symbol: e.target.value.toUpperCase() })} placeholder="USD/INR" /></div>
            <div>
              <Label>Source</Label>
              <select data-testid="mkt-inst-source" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm" value={instForm.source} onChange={(e) => setInstForm({ ...instForm, source: e.target.value })}>
                <option value="manual">I maintain it</option>
                <option value="live">Live (free feed)</option>
                <option value="alpha_vantage">Live (Alpha Vantage)</option>
              </select>
            </div>
            <div><Label>Country</Label><Input data-testid="mkt-inst-country" value={instForm.country} onChange={(e) => setInstForm({ ...instForm, country: e.target.value.toUpperCase() })} /></div>
            <div><Label>Currency</Label><Input data-testid="mkt-inst-currency" value={instForm.currency} onChange={(e) => setInstForm({ ...instForm, currency: e.target.value.toLowerCase() })} /></div>
            <div><Label>Bond maturity (years)</Label><Input data-testid="mkt-inst-maturity" type="number" min={1} value={instForm.maturity_years} onChange={(e) => setInstForm({ ...instForm, maturity_years: e.target.value })} /></div>
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-600">
              <input data-testid="mkt-inst-public" type="checkbox" checked={instForm.public} onChange={(e) => setInstForm({ ...instForm, public: e.target.checked })} />
              Show on the public page
            </label>
          </div>
          <Button className="mt-3" data-testid="mkt-inst-submit" onClick={addInstrument}>Add it</Button>
        </div>
      )}
    </div>
  );
}
